/**
 * PlaceholderList - Dropdown component for placeholder autocomplete suggestions
 *
 * Renders a polished, shadcn-inspired list of available placeholders
 * when the user types a trigger character.
 * Supports keyboard navigation and selection.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PlaceholderChip from '../common/PlaceholderChip';
import './TipTapEditor.css';

const mentionSectionLabel = (type) => (type === 'team' ? 'Teams' : 'Agents');

const buildMentionRows = (items) => {
  const rows = [];
  let previousType = null;

  items.forEach((item, index) => {
    const sectionType = item.type === 'team' ? 'team' : 'agent';
    if (sectionType !== previousType) {
      if (rows.length > 0) {
        rows.push({ kind: 'divider' });
      }
      rows.push({ kind: 'header', label: mentionSectionLabel(sectionType) });
      previousType = sectionType;
    }
    rows.push({ kind: 'item', item, index });
  });

  return rows;
};

const PlaceholderList = forwardRef(({ items, command, triggerChar }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const trigger = triggerChar || '$';
  const isAgentMention = trigger === '@';

  const rows = useMemo(() => {
    if (!isAgentMention) {
      return (items || []).map((item, index) => ({ kind: 'item', item, index }));
    }
    return buildMentionRows(items || []);
  }, [items, isAgentMention]);

  const selectableRows = useMemo(
    () => rows.filter((row) => row.kind === 'item'),
    [rows]
  );

  const selectItem = useCallback((index) => {
    const row = selectableRows[index];
    const item = row?.item;
    if (item && command) {
      command(item);
    }
  }, [selectableRows, command]);

  const upHandler = useCallback(() => {
    setSelectedIndex((prev) =>
      (prev + selectableRows.length - 1) % selectableRows.length
    );
  }, [selectableRows.length]);

  const downHandler = useCallback(() => {
    setSelectedIndex((prev) =>
      (prev + 1) % selectableRows.length
    );
  }, [selectableRows.length]);

  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectItem, selectedIndex]);

  useEffect(() => {
    setSelectedIndex(0);
    itemRefs.current = [];
  }, [rows]);

  useEffect(() => {
    const selectedElement = itemRefs.current[selectedIndex];
    if (selectedElement) {
      selectedElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  if (!selectableRows.length) {
    return (
      <div className="placeholder-suggestion-popup">
        <div className="placeholder-list-container">
          <div className="placeholder-list-empty">
            No matching placeholders
          </div>
        </div>
      </div>
    );
  }

  let selectableIndex = -1;

  return (
    <div className="placeholder-suggestion-popup">
      <div className="placeholder-list-container" ref={containerRef}>
        {rows.map((row, rowIndex) => {
          if (row.kind === 'header') {
            return (
              <div
                key={`header-${row.label}-${rowIndex}`}
                className="placeholder-list-header"
              >
                {row.label}
              </div>
            );
          }

          if (row.kind === 'divider') {
            return (
              <div
                key={`divider-${rowIndex}`}
                className="placeholder-list-divider"
                aria-hidden="true"
              />
            );
          }

          selectableIndex += 1;
          const item = row.item;
          const isSelected = selectableIndex === selectedIndex;

          return (
            <button
              key={`${item.id || item.key}-${rowIndex}`}
              ref={(el) => {
                itemRefs.current[selectableIndex] = el;
              }}
              className={`placeholder-list-item ${isSelected ? 'selected' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectItem(selectableIndex);
              }}
              onMouseEnter={() => setSelectedIndex(selectableIndex)}
              onMouseDown={(e) => e.preventDefault()}
              type="button"
            >
              <PlaceholderChip
                placeholderKey={item.key}
                prefix={isAgentMention ? '@' : trigger}
                type={item.type}
                length={item.length}
                size="small"
              >
                {!isAgentMention && item.label
                  ? `${trigger}{${item.label}}`
                  : undefined}
              </PlaceholderChip>
              {(isAgentMention ? item.description : item.type) && (
                <span className="placeholder-list-item-description">
                  {isAgentMention
                    ? (item.description.length > 40
                        ? item.description.slice(0, 40) + '…'
                        : item.description)
                    : item.type}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

PlaceholderList.displayName = 'PlaceholderList';

export default PlaceholderList;
